/**
 * AppSidebar — persistent CRM-style left navigation shown across every
 * signed-in (/app/*) screen, including client dashboards.
 *
 * Modules the client doesn't have appear greyed/locked; clicking one opens the
 * ModuleEnquiryModal which raises an enquiry in the operator queue + emails us.
 * Role-only areas (People / Advisory) stay hidden for non-managers.
 */
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { authGet } from '@/utils/authFetch';
import {
  BarChart3, TrendingUp, Globe, User, Home,
  Search, Plug2, Menu, Scale, Mail, Users, ArrowRightLeft,
  Briefcase, Target, Wallet, Sparkles, Layers, Brain, Lock, ChevronRight, ShieldCheck,
} from 'lucide-react';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger,
} from '@/components/ui/sheet';
import { useClient } from '@/contexts/ClientContext';
import ModuleEnquiryModal from './ModuleEnquiryModal';

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

export default function AppSidebar() {
  const location = useLocation();
  const { clientType } = useClient();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [enquiry, setEnquiry] = useState({ open: false, moduleName: '', moduleId: null });
  // Awaiting-review partner intake count for the Management sidebar badge
  // (operator-only endpoint; silently stays 0 for everyone else).
  const [reviewCount, setReviewCount] = useState(0);
  useEffect(() => {
    let alive = true;
    const refresh = () => authGet('/api/partner-admin/intake/requests')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (alive && d) setReviewCount(d?.counts?.submitted || 0); })
      .catch(() => {});
    refresh();
    window.addEventListener('corvana:intake-reviewed', refresh);
    return () => { alive = false; window.removeEventListener('corvana:intake-reviewed', refresh); };
  }, []);

  const isActive = (path) => {
    if (path === '/app') {
      return ['/app', '/app/dashboard', '/app/my-dashboard'].includes(location.pathname);
    }
    return location.pathname === path;
  };
  const hasPermission = (permission) => !permission || clientType.permissions.includes(permission);
  const isOwnerOrManager = (clientType?.permissions?.length || 0) >= 3;

  // permission → each item may be gated; gated items the client lacks become
  // "locked" (greyed + click-to-enquire) rather than hidden.
  const groups = [
    {
      label: 'Dashboard',
      items: [
        { path: '/app', label: 'Dashboard', icon: Home },
        { path: '/app/modules', label: 'My Modules', icon: Layers },
        { path: '/app/capabilities', label: 'What We Can Do', icon: Sparkles },
      ],
    },
    {
      label: 'Operations',
      items: [
        { path: '/app/management', label: 'Management', icon: BarChart3, permission: 'management', moduleId: 'operations', showReviewBadge: true },
      ],
    },
    {
      label: 'Intelligence',
      items: [
        { path: '/app/corvana-ai', label: 'Corvana AI', icon: Brain },
        { path: '/app/market-intelligence', label: 'Market Intel', icon: TrendingUp, permission: 'market_intelligence', moduleId: 'market_intel' },
        { path: '/app/marketing-intelligence', label: 'Marketing', icon: Globe, permission: 'marketing_intelligence', moduleId: 'marketing' },
        { path: '/app/email-marketing', label: 'Email Marketing', icon: Mail, permission: 'marketing_intelligence', moduleId: 'marketing' },
        { path: '/app/financials', label: 'Financials', icon: Wallet, permission: 'projections', moduleId: 'projections' },
        { path: '/app/action-plans', label: 'Action Plans', icon: Target },
        { path: '/app/business-search', label: 'Business Search', icon: Search, permission: 'business_search', moduleId: 'business_search' },
        { path: '/app/regulatory-compliance', label: 'Regulatory', icon: Scale, permission: 'view_overview', moduleId: 'compliance' },
        { path: '/app/legal', label: 'Legal Assistant', icon: Scale, permission: 'view_overview', moduleId: 'legal' },
        { path: '/app/forensic-accounting', label: 'Forensic Finance', icon: Scale, permission: 'view_overview', moduleId: 'financial' },
        { path: '/app/verified-financials', label: 'Verified Financials', icon: ShieldCheck },
        { path: '/app/bookkeeper', label: 'Bookkeeper', icon: Wallet, permission: 'view_overview', moduleId: 'bookkeeper' },
      ],
    },
    isOwnerOrManager && {
      label: 'People',
      items: [
        { path: '/app/team', label: 'Team Management', icon: Users },
        { path: '/app/staff-transitions', label: 'Transitions', icon: ArrowRightLeft },
        { path: '/app/staffpro', label: 'StaffPro', icon: Briefcase },
      ],
    },
    isOwnerOrManager && {
      label: 'Advisory',
      items: [
        { path: '/app/partner-command-centre', label: 'Command Centre', icon: Sparkles },
        { path: '/app/accountant-portal', label: 'Client Portfolio', icon: Briefcase },
        { path: '/app/bulk-onboarding', label: 'Bulk Onboarding', icon: Users },
      ],
    },
    {
      label: 'Settings',
      items: [
        { path: '/app/account', label: 'Account', icon: User },
        isOwnerOrManager && { path: '/app/settings/modules', label: 'Modules & Integrations', icon: Plug2 },
      ].filter(Boolean),
    },
  ].filter(Boolean);

  const openEnquiry = (item) => setEnquiry({ open: true, moduleName: item.label, moduleId: item.moduleId || null });

  const renderItem = (item, onNavigate) => {
    const Icon = item.icon;
    const locked = item.permission && !hasPermission(item.permission);
    const active = isActive(item.path);
    const testId = slug(item.label);

    if (locked) {
      return (
        <button
          key={item.path}
          type="button"
          onClick={() => { onNavigate?.(); openEnquiry(item); }}
          data-testid={`app-sidebar-locked-${testId}`}
          title={`${item.label} isn't on your plan — click to enquire`}
          className="group w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm text-slate-400 hover:text-slate-600 hover:bg-slate-100/70 transition-all duration-200"
        >
          <Icon className="w-4 h-4 shrink-0 opacity-70" />
          <span className="flex-1 text-left truncate">{item.label}</span>
          <Lock className="w-3.5 h-3.5 shrink-0 opacity-50 group-hover:opacity-100 transition-opacity" />
        </button>
      );
    }

    return (
      <Link
        key={item.path}
        to={item.path}
        onClick={onNavigate}
        data-testid={`app-sidebar-item-${testId}`}
        className={cn(
          'group relative w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm transition-all duration-200',
          active
            ? 'bg-gradient-to-r from-teal-50 to-blue-50 text-slate-900 font-semibold ring-1 ring-blue-100'
            : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100/70 hover:translate-x-0.5',
        )}
      >
        {active && <span className="absolute left-0 top-1.5 bottom-1.5 w-1 rounded-full bg-gradient-to-b from-teal-500 to-blue-600" />}
        <Icon className={cn('w-4 h-4 shrink-0', active ? 'text-blue-600' : 'text-slate-400 group-hover:text-slate-600')} />
        <span className="flex-1 truncate">{item.label}</span>
        {item.showReviewBadge && reviewCount > 0 && (
          <span data-testid="sidebar-review-badge"
            className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-emerald-600 text-white text-[10px] font-bold">
            {reviewCount}
          </span>
        )}
        {active && <ChevronRight className="w-3.5 h-3.5 text-blue-500/70 shrink-0" />}
      </Link>
    );
  };

  const navBody = (onNavigate) => (
    <nav className="space-y-5" data-testid="app-sidebar-nav">
      {groups.map((group) => (
        <div key={group.label}>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 px-3 mb-1.5 leading-none">
            {group.label}
          </div>
          <div className="space-y-0.5">
            {group.items.map((item) => renderItem(item, onNavigate))}
          </div>
        </div>
      ))}
    </nav>
  );

  return (
    <>
      {/* Mobile top bar with hamburger */}
      <div className="lg:hidden px-4 sm:px-6 pt-3">
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <button
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-white/70 backdrop-blur border border-slate-200 text-sm font-medium text-slate-700 hover:bg-white transition-colors"
              data-testid="app-sidebar-mobile-trigger"
            >
              <Menu className="w-4 h-4" /> Menu
            </button>
          </SheetTrigger>
          <SheetContent side="left" className="w-[280px] sm:w-[300px] bg-white/95 backdrop-blur-2xl overflow-y-auto">
            <SheetHeader>
              <SheetTitle className="text-left">Navigation</SheetTitle>
            </SheetHeader>
            <div className="mt-5">{navBody(() => setMobileOpen(false))}</div>
          </SheetContent>
        </Sheet>
      </div>

      {/* Desktop persistent sidebar */}
      <aside
        className="hidden lg:block w-64 shrink-0 sticky top-[72px] h-[calc(100vh-72px)] overflow-y-auto border-r border-slate-200/70 bg-white/60 backdrop-blur-xl px-3 py-6"
        data-testid="app-sidebar"
      >
        {navBody()}
      </aside>

      <ModuleEnquiryModal
        open={enquiry.open}
        onOpenChange={(v) => setEnquiry((e) => ({ ...e, open: v }))}
        moduleName={enquiry.moduleName}
        moduleId={enquiry.moduleId}
      />
    </>
  );
}
